import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AlertTriangle, Mail, KeyRound, CheckCircle2, X } from "lucide-react";

/**
 * Forces a student with a temporary email to claim their account before they
 * can download online-course certificates. Renders as a modal; on success
 * calls onClaimed() so the parent can refresh the user session.
 */
export default function ClaimAccountModal({ onClose, onClaimed, message }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) { setErr("Ingresa tu correo real"); return; }
    if (pw.length < 8) { setErr("La contraseña debe tener al menos 8 caracteres"); return; }
    if (pw !== pw2) { setErr("Las contraseñas no coinciden"); return; }
    setBusy(true);
    try {
      await api.post("/users/me/claim-account", { new_email: email.trim().toLowerCase(), new_password: pw });
      onClaimed?.(email.trim().toLowerCase());
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="claim-account-modal" className="fixed inset-0 z-[100] bg-marino/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
        <div className="bg-marino text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-bold">
            <AlertTriangle className="w-5 h-5 text-naranja" /> Reclama tu cuenta
          </div>
          {onClose && (
            <button data-testid="claim-close" onClick={onClose} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
          )}
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="flex gap-3 bg-naranja/5 border-l-4 border-naranja rounded-lg p-3">
            <span className="w-5 h-5 text-naranja flex-shrink-0 mt-0.5">⚠️</span>
            <p className="text-marino text-sm leading-relaxed">
              {message || "Para descargar tus certificados, por favor ingresa tu correo electrónico real y cambia tu contraseña."}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-marino/70 mb-1">
              <Mail className="w-3.5 h-3.5 inline mr-1" /> Correo real
            </label>
            <input
              data-testid="claim-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tucorreo@ejemplo.com"
              className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-marino/70 mb-1">
              <KeyRound className="w-3.5 h-3.5 inline mr-1" /> Nueva contraseña
            </label>
            <input
              data-testid="claim-password"
              type="password"
              required
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
            />
            <p className="text-[11px] text-marino/50 mt-1">Mínimo 8 caracteres.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-marino/70 mb-1">Confirma la contraseña</label>
            <input
              data-testid="claim-password-2"
              type="password"
              required
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
            />
          </div>

          {err && (
            <div data-testid="claim-error" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>
          )}

          <button
            data-testid="claim-submit"
            disabled={busy}
            className="w-full bg-naranja text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-naranjaHover inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <CheckCircle2 className="w-4 h-4" /> {busy ? "Guardando..." : "Guardar y continuar"}
          </button>
        </form>
      </div>
    </div>
  );
}
